import { useMutation } from '@tanstack/react-query';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { usersService, User } from '@/services/users.service';
import toast from 'react-hot-toast';

interface DeleteUserDialogProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function DeleteUserDialog({ user, isOpen, onClose, onSuccess }: DeleteUserDialogProps) {
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => usersService.deleteUser(userId),
    onSuccess: () => {
      onSuccess();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message ?? 'Failed to delete user');
    },
  });

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(user.id);
  };

  if (!isOpen) { return null; }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="flex items-start justify-center min-h-screen p-4 pt-16">
        <div className="relative bg-white dark:bg-secondary-900 rounded-lg shadow-xl max-w-md w-full mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-danger-100 dark:bg-danger-900/20 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-6 h-6 text-danger-600 dark:text-danger-400" />
              </div>
              <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                Delete User
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
              disabled={deleteMutation.isPending}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-secondary-700 dark:text-secondary-300 mb-4">
              Are you sure you want to delete this user? This action will soft delete the account and it can be restored later by an administrator.
            </p>

            <div className="bg-secondary-50 dark:bg-secondary-800 rounded-lg p-4 mb-4">
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                    Name
                  </dt>
                  <dd className="text-sm text-secondary-900 dark:text-white mt-1">
                    {user.fullName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                    Email
                  </dt>
                  <dd className="text-sm text-secondary-900 dark:text-white mt-1">
                    {user.email}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
                    Roles
                  </dt>
                  <dd className="text-sm text-secondary-900 dark:text-white mt-1">
                    {user.roles.length > 0
                      ? user.roles.map(r => usersService.getRoleDisplayName(r)).join(', ')
                      : 'No roles'}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4">
              <p className="text-sm text-warning-800 dark:text-warning-300">
                <strong>Note:</strong> This is a soft delete. The user account will be deactivated and marked as deleted, but the data will be retained in the system.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 dark:border-secondary-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete User'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
